import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { format } from "date-fns";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { Order, stableSort, getComparator } from "../ui/Tables";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { useEffect, useState } from "react";
import { Loading } from "../ui/Loading";
import { uiStore } from "../uiStore";
import { DatePicker } from "@mui/x-date-pickers";
import Stack from "@mui/material/Stack";
import { createDateAsUTC, readDate } from "../utils/dateUtils";
import { EuroFormatter } from "../utils/EuroFormatter";
import { Link } from "@mui/material";
import * as ExcelJS from "exceljs";
import { downloadExcel } from "../utils/excel";

interface Data {
  denominazione: string;
  id: number;
  dataInizioPrenotazione: string;
  dataFinePrenotazione: string;
  durata: string;
  codiceSocio: string;
  codiceCard: string;
  cognome: string;
  nome: string;
  tipologiAassoc: string;
  descrizioneUso: string;
  cifraAddebitata: string;
  dataDisdetta: string;
  dataInserimento: string;
  riscaldamento: string;
  luce: string;
  luciPost: string;
  riscPost: string;
}
interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
  key: string;
  filter: boolean;
  filterWidth?: number;
}

const headCells: readonly HeadCell[] = [
  {
    id: "denominazione",
    numeric: false,
    disablePadding: false,
    label: "Risorsa",
    key: "denominazione",
    filter: true,
  },
  {
    id: "dataInizioPrenotazione",
    numeric: false,
    disablePadding: false,
    label: "Data",
    key: "data",
    filter: false,
    filterWidth: 120,
  },
  {
    id: "dataInizioPrenotazione",
    numeric: false,
    disablePadding: false,
    label: "Ora Inizio",
    key: "dataIn",
    filter: false,
    filterWidth: 60,
  },
  {
    id: "dataFinePrenotazione",
    numeric: false,
    disablePadding: false,
    label: "Ora Fine",
    key: "dataFi",
    filter: false,
    filterWidth: 60,
  },
  {
    id: "durata",
    numeric: false,
    disablePadding: false,
    label: "Durata",
    key: "durata",
    filter: false,
    filterWidth: 60,
  },
  {
    id: "codiceSocio",
    numeric: false,
    disablePadding: false,
    label: "Codice Socio",
    key: "codi",
    filter: true,
    filterWidth: 100,
  },
  {
    id: "codiceCard",
    numeric: false,
    disablePadding: false,
    label: "Codice Card",
    key: "codiceCard",
    filter: true,
    filterWidth: 100,
  },
  {
    id: "nome",
    numeric: false,
    disablePadding: false,
    label: "Nome",
    key: "nome",
    filter: true,
    filterWidth: 120,
  },
  {
    id: "cognome",
    numeric: false,
    disablePadding: false,
    label: "Cognome",
    key: "cognome",
    filter: true,
    filterWidth: 120,
  },
  {
    id: "tipologiAassoc",
    numeric: false,
    disablePadding: false,
    label: "Tipo Assoc",
    key: "tipologiAassoc",
    filter: true,
    filterWidth: 120,
  },
  {
    id: "descrizioneUso",
    numeric: false,
    disablePadding: false,
    label: "Tipo Uso",
    key: "descrizioneUso",
    filter: true,
    filterWidth: 120,
  },
  {
    id: "cifraAddebitata",
    numeric: false,
    disablePadding: false,
    label: "Importo",
    key: "cifraAddebitata",
    filter: true,
    filterWidth: 80,
  },
  {
    id: "luce",
    numeric: false,
    disablePadding: false,
    label: "Luce",
    key: "luce",
    filter: true,
    filterWidth: 50,
  },
  {
    id: "riscaldamento",
    numeric: false,
    disablePadding: false,
    label: "Riscaldamento",
    key: "riscaldamento",
    filter: true,
    filterWidth: 50,
  },
  {
    id: "dataDisdetta",
    numeric: false,
    disablePadding: false,
    label: "Data Disdetta",
    key: "dataDisdetta",
    filter: false,
    filterWidth: 80,
  },
  {
    id: "dataInserimento",
    numeric: false,
    disablePadding: false,
    label: "Data Inserimento",
    key: "dataInserimento",
    filter: false,
    filterWidth: 80,
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  onFilter: (event: any, property: keyof Data, filter: string | number) => void;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, onFilter } = props;
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };
  const createFilterHandler = (property: keyof Data) => (event: any) => {
    onFilter(event, property, event.target.value);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.key || headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.key ? headCell.key : headCell.id}>
            {headCell.filter && (
              <TextField
                label=""
                variant="standard"
                onChange={createFilterHandler(headCell.id)}
                sx={{ maxWidth: headCell.filterWidth ?? 300 }}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const getData = (date: Date | null) => {
  if (date === null) return "";
  if (isNaN(date.getTime())) return "";
  const data2 = createDateAsUTC(date);
  data2.setUTCHours(0);
  data2.setUTCMinutes(0);
  data2.setSeconds(0);
  data2.setMilliseconds(0);
  return data2?.toISOString() ?? "";
};

export function Statistiche() {
  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => setTitle("Statistiche"), [setTitle]);

  const [dataInizio, setInizio] = useState<Date | null>(null);
  const [dataFine, setFine] = useState<Date | null>(null);

  const [rows, setRows] = useState<Data[]>([]);

  const { isLoading, refetch } = useQuery<unknown, AxiosError, Data[]>(
    ["prenotazioni/getPrenotazioniStats", dataInizio, dataFine],
    () =>
      axios
        .post(
          API_URL +
            "prenotazioni/getPrenotazioniStats/" +
            getData(dataInizio) +
            "/" +
            getData(dataFine)
        )
        .then((res) => res.data),
    {
      enabled: false,
      onSuccess(data) {
        setRows(data);
      },
    }
  );

  const filtra = () => {
    if (dataInizio !== null && dataFine !== null) refetch();
  };

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("dataInizioPrenotazione");
  const [page, setPage] = React.useState(0);
  const dense = oraPrenotaStore((state) => state.tabelleCompatte);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [filters, setFilters] = useState({});
  const handleFilterRequest = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
    filter: string | number
  ) => {
    setFilters({
      ...filters,
      [property]: filter,
    });
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const prenotazioni = stableSort(rows, getComparator(order, orderBy), filters);

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Transazioni");
    sheet.columns = [
      { header: "Denominazione", key: "denominazione", width: 40 },
      {
        header: "Data Inizio",
        key: "dataInizioPrenotazione",
        width: 20,
        style: { numFmt: "dd/mm/yyyy HH:MM" },
      },
      {
        header: "Data Fine",
        key: "dataFinePrenotazione",
        width: 20,
        style: { numFmt: "dd/mm/yyyy HH:MM" },
      },
      { header: "Durata", key: "durata", width: 20 },
      { header: "Codice Card", key: "codiceCard", width: 20 },
      { header: "Codice Socio", key: "codiceSocio", width: 20 },
      { header: "Nome", key: "nome", width: 30 },
      { header: "Cognome", key: "cognome", width: 30 },
      { header: "Tipologia Assoc", key: "tipologiAassoc", width: 30 },
      { header: "Descrizione Uso", key: "descrizioneUso", width: 30 },
      {
        header: "Cifra Addebitata",
        key: "cifraAddebitata",
        width: 15,
        style: { numFmt: "#,##0.00" },
      },
      { header: "Luce", key: "luce", width: 15 },
      { header: "Riscaldamento", key: "riscaldamento", width: 15 },
      {
        header: "Data Disdetta",
        key: "dataDisdetta",
        width: 20,
        style: { numFmt: "dd/mm/yyyy HH:MM" },
      },
      {
        header: "Data Inserimento",
        key: "dataInserimento",
        width: 20,
        style: { numFmt: "dd/mm/yyyy HH:MM" },
      },
    ];

    prenotazioni.forEach((row) =>
      sheet.addRow({
        denominazione: row.denominazione,
        dataInizioPrenotazione: readDate(row.dataInizioPrenotazione),
        dataFinePrenotazione: readDate(row.dataFinePrenotazione),
        durata: row.durata.substring(0, 5),
        codiceSocio: row.codiceSocio,
        codiceCard: row.codiceCard,
        nome: row.nome,
        cognome: row.cognome,
        tipologiAassoc: row.tipologiAassoc,
        descrizioneUso: row.descrizioneUso,
        cifraAddebitata: row.cifraAddebitata,
        luce: row.luce ? "Si" : "No",
        riscaldamento: row.riscaldamento ? "Si" : "No",
        dataDisdetta: row.dataDisdetta > 0 ? readDate(row.dataDisdetta) : "",
        dataInserimento: readDate(row.dataInserimento),
      })
    );

    const buff = await workbook.xlsx.writeBuffer();
    downloadExcel(buff, "prenotazioni.xlsx");
  };

  return (
    <Stack sx={{ width: "100%" }} direction={"column"} gap={2}>
      <Stack
        direction={["column", "row"]}
        gap={3}
        justifyContent={["center", "center", "flex-start"]}
        alignItems="center"
        sx={{ flexWrap: "wrap" }}
      >
        <DatePicker
          label="Dal"
          value={dataInizio}
          onChange={(newValue) => {
            setInizio(newValue);
          }}
          renderInput={(params) => (
            <TextField {...params} variant="standard" sx={{ width: 150 }} type="date" />
          )}
        />
        <DatePicker
          label="Al"
          value={dataFine}
          onChange={(newValue) => {
            setFine(newValue);
          }}
          renderInput={(params) => (
            <TextField {...params} variant="standard" sx={{ width: 150 }} type="date" />
          )}
        />
        <Button type="submit" variant="contained" onClick={() => filtra()}>
          Filtra
        </Button>{" "}
        <Button
          disabled={!(dataInizio !== null && dataFine !== null)}
          color="secondary"
          variant="contained"
          component={Link}
          target={"_blank"}
          href={"../prenotazioni/getExcel/" + getData(dataInizio) + "/" + getData(dataFine)}
        >
          Excel (tutte)
        </Button>{" "}
        <Button variant="contained" color="success" onClick={() => exportExcel()}>
          Excel (visualizzate)
        </Button>
      </Stack>

      {isLoading && <Loading />}
      {!isLoading && (
        <>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
                onFilter={handleFilterRequest}
              />
              <TableBody classes={{ root: "table-body" }}>
                {prenotazioni
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    return (
                      <TableRow hover tabIndex={-1} key={row.id}>
                        <TableCell>{row.denominazione}</TableCell>
                        <TableCell>
                          {format(readDate(row.dataInizioPrenotazione), "dd/MM/yyyy")}
                        </TableCell>
                        <TableCell>
                          {format(readDate(row.dataInizioPrenotazione), "HH:mm")}
                        </TableCell>
                        <TableCell>{format(readDate(row.dataFinePrenotazione), "HH:mm")}</TableCell>
                        <TableCell>{row.durata.substring(0, 5)}</TableCell>
                        <TableCell>{row.codiceSocio}</TableCell>
                        <TableCell>{row.codiceCard}</TableCell>
                        <TableCell>{row.nome}</TableCell>
                        <TableCell>{row.cognome}</TableCell>
                        <TableCell>{row.tipologiAassoc}</TableCell>
                        <TableCell>{row.descrizioneUso}</TableCell>
                        <TableCell>{EuroFormatter.format(row.cifraAddebitata)}</TableCell>
                        <TableCell>{row.luce ? "Si" : "No"}</TableCell>
                        <TableCell>{row.riscaldamento ? "Si" : "No"}</TableCell>
                        <TableCell>
                          {row.dataDisdetta > 0
                            ? format(readDate(row.dataDisdetta), "dd/MM/yyyy HH:mm")
                            : ""}
                        </TableCell>
                        <TableCell>
                          {format(readDate(row.dataInserimento), "dd/MM/yyyy HH:mm")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Stack>
  );
}
